import { useEffect, useRef } from "react";

/**
 * Pushes a browser history entry when `active` is true and calls `onClose`
 * when the user presses the browser back button.
 *
 * - When back is pressed: the pushed state is automatically popped, and
 *   `onClose` fires.
 * - When closed programmatically (active becomes false without a popstate):
 *   we call `history.back()` to clean up the pushed entry.
 */
export function useBackButtonClose(active: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;

    const backCloseId = `back-close-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`;
    let closedViaPopState = false;

    // Preserve existing router state while tagging this entry as "closeable".
    const existingState = window.history.state;
    const nextState =
      existingState && typeof existingState === "object"
        ? {
            ...(existingState as Record<string, unknown>),
            backCloseId: backCloseId,
          }
        : { backCloseId: backCloseId };

    window.history.pushState(nextState, "");

    const handlePopState = () => {
      closedViaPopState = true;
      onCloseRef.current();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // If closed programmatically, pop the history entry we pushed — but only
      // if it's still the current entry. This prevents accidental "back"
      // navigations if the user routed elsewhere while the sheet was open.
      if (closedViaPopState) return;
      const currentState = window.history.state;
      if (
        currentState &&
        typeof currentState === "object" &&
        (currentState as Record<string, unknown>).backCloseId === backCloseId
      ) {
        window.history.back();
      }
    };
  }, [active]);
}
